import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';

export type PlanType = 'free' | 'basic' | 'pro' | 'enterprise' | 'admin';

export interface SubscriptionPlan {
  type: PlanType;
  name: string;
  reelsLimit: number; // -1 for unlimited
  hasAnalytics: boolean;
  hasAdvancedScheduling: boolean;
  hasBrandingControl: boolean;
  hasPrioritySupport: boolean;
  has4KExport: boolean;
  hasTeamCollaboration: boolean;
  hasWhiteLabel: boolean;
  hasApiAccess: boolean;
  hasByoaOption: boolean; // Bring Your Own API option
  byoaDiscount: number; // Tier-specific BYOA discount
}

export interface UserSubscription {
  plan: SubscriptionPlan;
  isActive: boolean;
  currentPeriodEnd: Date;
  reelsUsedThisMonth: number;
  hasByoaAddon: boolean; // User opted for BYOA discount
  stripeCustomerId?: string;
  stripeSubscriptionId?: string;
}

interface SubscriptionContextType {
  subscription: UserSubscription | null;
  loading: boolean;
  canCreateReel: boolean;
  getRemainingReels: () => number;
  updateSubscription: (planType: PlanType, hasByoaAddon?: boolean) => Promise<void>;
  cancelSubscription: () => Promise<void>;
  hasFeature: (feature: keyof SubscriptionPlan) => boolean;
  getUpgradeUrl: (planType: PlanType) => string;
}

// Admin plan included in main type definition above

const plans: Record<PlanType, SubscriptionPlan> = {
  free: {
    type: 'free',
    name: 'Free',
    reelsLimit: 3,
    hasAnalytics: false,
    hasAdvancedScheduling: false,
    hasBrandingControl: false,
    hasPrioritySupport: false,
    has4KExport: false,
    hasTeamCollaboration: false,
    hasWhiteLabel: false,
    hasApiAccess: false,
    hasByoaOption: false,
    byoaDiscount: 0,
  },
  basic: {
    type: 'basic',
    name: 'Basic',
    reelsLimit: 30, // Updated: reduced for economic viability
    hasAnalytics: true,
    hasAdvancedScheduling: false,
    hasBrandingControl: false,
    hasPrioritySupport: false,
    has4KExport: false,
    hasTeamCollaboration: false,
    hasWhiteLabel: false,
    hasApiAccess: false,
    hasByoaOption: true,
    byoaDiscount: 20, // Updated: higher discount for BYOA ($39 -> $19)
  },
  pro: {
    type: 'pro',
    name: 'Pro',
    reelsLimit: 100, // Updated: reduced for economic viability
    hasAnalytics: true,
    hasAdvancedScheduling: true,
    hasBrandingControl: true,
    hasPrioritySupport: true,
    has4KExport: true,
    hasTeamCollaboration: false,
    hasWhiteLabel: false,
    hasApiAccess: false,
    hasByoaOption: true,
    byoaDiscount: 60, // Updated: higher discount for BYOA ($149 -> $89)
  },
  enterprise: {
    type: 'enterprise',
    name: 'Enterprise',
    reelsLimit: 200, // Updated: set limit for cost control
    hasAnalytics: true,
    hasAdvancedScheduling: true,
    hasBrandingControl: true,
    hasPrioritySupport: true,
    has4KExport: true,
    hasTeamCollaboration: true,
    hasWhiteLabel: true,
    hasApiAccess: true,
    hasByoaOption: true,
    byoaDiscount: 100, // Updated: higher discount for BYOA ($399 -> $299)
  },
  admin: {
    type: 'admin',
    name: 'Admin',
    reelsLimit: -1, // Unlimited
    hasAnalytics: true,
    hasAdvancedScheduling: true,
    hasBrandingControl: true,
    hasPrioritySupport: true,
    has4KExport: true,
    hasTeamCollaboration: true,
    hasWhiteLabel: true,
    hasApiAccess: true,
    hasByoaOption: true,
    byoaDiscount: 0, // No discount needed for admin
  },
};

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

interface SubscriptionProviderProps {
  children: ReactNode;
}

export const SubscriptionProvider: React.FC<SubscriptionProviderProps> = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [subscription, setSubscription] = useState<UserSubscription | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isAuthenticated && user) {
      fetchSubscription();
    } else {
      setSubscription(null);
    }
  }, [isAuthenticated, user]);

  const fetchSubscription = async () => {
    setLoading(true);
    try {
      // TODO: Replace with actual API call
      // const response = await api.get('/subscription/');
      
      // Check if user is admin
      const isAdmin = user?.is_admin === true;
      
      const mockSubscription: UserSubscription = {
        plan: isAdmin ? plans.admin : plans.free,
        isActive: true,
        currentPeriodEnd: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000), // 1 year for admin
        reelsUsedThisMonth: 0,
        hasByoaAddon: isAdmin ? true : false, // Admin gets BYOA access
      };
      
      setSubscription(mockSubscription);
    } catch (error) {
      console.error('Failed to fetch subscription:', error);
      // Default to free plan on error
      setSubscription({
        plan: plans.free,
        isActive: true,
        currentPeriodEnd: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
        reelsUsedThisMonth: 0,
        hasByoaAddon: false,
      });
    } finally {
      setLoading(false);
    }
  };

  const canCreateReel = React.useMemo(() => {
    if (!subscription) return false;
    if (subscription.plan.reelsLimit === -1) return true; // Unlimited
    return subscription.reelsUsedThisMonth < subscription.plan.reelsLimit;
  }, [subscription]);

  const getRemainingReels = () => {
    if (!subscription) return 0;
    if (subscription.plan.reelsLimit === -1) return Infinity;
    return Math.max(0, subscription.plan.reelsLimit - subscription.reelsUsedThisMonth);
  };

  const hasFeature = (feature: keyof SubscriptionPlan) => {
    if (!subscription) return false;
    return subscription.plan[feature] as boolean;
  };

  const updateSubscription = async (planType: PlanType, hasByoaAddon: boolean = false) => {
    try {
      // TODO: Implement Stripe integration
      // const response = await api.post('/subscription/update', { planType, hasByoaAddon });
      
      const newPlan = plans[planType];
      setSubscription(prev => prev ? {
        ...prev,
        plan: newPlan,
        hasByoaAddon: hasByoaAddon && newPlan.hasByoaOption,
      } : null);
    } catch (error) {
      console.error('Failed to update subscription:', error);
      throw error;
    }
  };

  const cancelSubscription = async () => {
    try {
      // TODO: Implement cancellation
      // await api.post('/subscription/cancel');
      
      setSubscription(prev => prev ? {
        ...prev,
        plan: plans.free,
        hasByoaAddon: false,
      } : null);
    } catch (error) {
      console.error('Failed to cancel subscription:', error);
      throw error;
    }
  };

  const getUpgradeUrl = (planType: PlanType) => {
    // TODO: Generate Stripe checkout URLs
    return `/subscribe/${planType}`;
  };

  const value = {
    subscription,
    loading,
    canCreateReel,
    getRemainingReels,
    updateSubscription,
    cancelSubscription,
    hasFeature,
    getUpgradeUrl,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export { plans };